import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

import { isNative, platform } from './platform';

/**
 * The four native-shell behaviours BP-11 §6 asks of `NativeShell`.
 *
 * Each one is a function that does nothing on the web, so the component that
 * calls them has no platform branch of its own. That is the point: a `if
 * (isNative())` in a React effect is a branch that only one of the two test
 * environments ever takes, and the one that is never taken is the one that
 * breaks.
 */

/**
 * §6's status-bar colour: the app's own slate-50, with dark text on it.
 *
 * `#f8fafc` is Tailwind's `slate-50`, which is what `ClientShell` paints the
 * page. The two have to agree or the top 24 px of a phone is a different
 * colour from the rest of the screen.
 */
export const STATUS_BAR_COLOUR = '#f8fafc';

/** `plugins` so a test can supply doubles; the real ones are the default. */
export interface ShellPlugins {
  statusBar: Pick<typeof StatusBar, 'setStyle' | 'setBackgroundColor' | 'setOverlaysWebView'>;
  splash: Pick<typeof SplashScreen, 'hide'>;
  app: Pick<typeof App, 'addListener' | 'exitApp'>;
}

const realPlugins: ShellPlugins = { statusBar: StatusBar, splash: SplashScreen, app: App };

/**
 * Paint the status bar to match the page (§6).
 *
 * `setBackgroundColor` is Android-only — on iOS the status bar is drawn over
 * the WebView and takes its colour from what is behind it, which is why
 * `viewport-fit=cover` and the safe-area insets matter there instead.
 */
export async function applyStatusBar(plugins: ShellPlugins = realPlugins): Promise<void> {
  if (!isNative()) {
    return;
  }
  await plugins.statusBar.setStyle({ style: Style.Light });
  if (platform() === 'android') {
    await plugins.statusBar.setOverlaysWebView({ overlay: false });
    await plugins.statusBar.setBackgroundColor({ color: STATUS_BAR_COLOUR });
  }
}

/**
 * Hide the launch splash, once the app has drawn something (§6).
 *
 * `capacitor.config.ts` sets `launchAutoHide: false` so this is the only
 * thing that hides it: a splash on a timer disappears while a slow first
 * paint is still blank, and a white screen reads as a crash.
 */
export async function hideSplash(plugins: ShellPlugins = realPlugins): Promise<void> {
  if (!isNative()) {
    return;
  }
  await plugins.splash.hide();
}

/**
 * Android's hardware back button (§6).
 *
 * Capacitor's default closes the app on every press, which loses a
 * half-filled booking form. This goes back while there is somewhere to go
 * back to, and exits only from the first screen — which is what Android
 * users expect and what Play's review looks for.
 *
 * Returns the unsubscribe, so a component can tidy up on unmount.
 */
export async function onBackButton(
  goBack: () => void,
  canGoBack: () => boolean,
  plugins: ShellPlugins = realPlugins,
): Promise<() => void> {
  if (!isNative() || platform() !== 'android') {
    return () => undefined;
  }
  const handle = await plugins.app.addListener('backButton', () => {
    if (canGoBack()) {
      goBack();
      return;
    }
    void plugins.app.exitApp();
  });
  return () => {
    void handle.remove();
  };
}
